'use client'

// NamedPrices abstraction (Overmind→server-props migration, Phase A proof).
//
// WHAT THIS IS
// The dedicated seam that supplies the CMS named-price tokens (`${ITEM_PRICE}`
// substituted into marketing copy by <Text>) to the components that render them,
// sourced from the SERVER-FETCHED region price bundle threaded as a prop — NOT
// from a render-time Overmind `state.namedPrices` read. It is the first concrete
// step of retiring Overmind: the whole namedPrices concern (the store field, the
// `namedPricesFetched`/`namedPricesLoading` flags and the `namedPricesRequested`
// fetch action) is collapsed behind ONE hook, `useNamedPrices()`, so the ~2
// component leaves that consumed those store fields (`components/Text`,
// `sections/Products/UpgradableWheelBaseCheckoutPage`) no longer import Overmind
// at all — they import this module. Deleting the last Overmind fallback here
// (see FALLBACK below) is the final namedPrices phase.
//
// DATA SOURCE
// lib/regionPageData.loadRegionPageData already fetches the region price bundle
// (getRegionPriceBundle -> bundle.namedPrices, a `[{ name, price }]` array). That
// same array is now returned as `namedPrices` and threaded into
// <NamedPricesProvider namedPrices={...}> in the App Router page island
// (RegionPageClient), so the value is available DURING the server SSR render and
// during the client's first render — identical on both sides (it is literally the
// same serialized array), which is what keeps the <Text> price substitution
// hydration-safe (SSR HTML === first client render, no React #418/#423).
//
// APP-ROUTER SAFETY
// This module is a `'use client'` boundary (it holds React context). It is
// imported ONLY by client components (components/Text, the checkout page island) —
// so it introduces no new server-component/RSC coupling. No next/router, no window
// at render.
//
// SERVER-PROPS ONLY (Overmind retirement, Phase D2)
// The transitional Overmind fallback (state.namedPrices + namedPricesRequested) is
// GONE: the hook returns purely the server-threaded region price array. In
// production every priced <Text> renders under the <NamedPricesProvider> that
// RegionPageClient establishes, fed the region price bundle from
// lib/regionPageData. `ensureRequested()` is kept as a NO-OP so the ~hundreds of
// <Text> leaves that call it from a mount effect stay untouched. FLAG: on a
// price-bundle fetch MISS the provider carries `[]` and there is no longer a client
// refetch (the former Overmind fallback) — priced tokens then render via the
// not-yet-fetched path (clearPrice) exactly as they did before any refetch resolved.

import React, { createContext, useContext } from 'react'

// Server-threaded namedPrices array, or null when no provider is present.
const NamedPricesContext = createContext(null)

export function NamedPricesProvider({ namedPrices = null, children }) {
  return (
    <NamedPricesContext.Provider value={namedPrices}>
      {children}
    </NamedPricesContext.Provider>
  )
}

/**
 * The single namedPrices accessor for price-token consumers.
 *
 * Returns the server-threaded priced-token dataset plus the derived fetched/loading
 * flags and a NO-OP `ensureRequested()` (kept so the <Text> mount-effect callers
 * stay unchanged). `namedPricesFetched` is true iff the provider carried a
 * non-empty array — the same gate <Text> uses to decide clearPrice vs replacePrice.
 *
 * @returns {{ namedPrices: any[], namedPricesFetched: boolean,
 *             namedPricesLoading: boolean, ensureRequested: () => void }}
 */
export function useNamedPrices() {
  const serverNamedPrices = useContext(NamedPricesContext)
  const hasServerPrices = Array.isArray(serverNamedPrices) && serverNamedPrices.length > 0

  return {
    namedPrices: hasServerPrices ? serverNamedPrices : [],
    namedPricesFetched: hasServerPrices,
    namedPricesLoading: false,
    ensureRequested: () => {},
  }
}

export { NamedPricesContext }
